import type { ProjectileFlavour } from '../game/projectileFlavour';
import type { AudioBus, Vec3Like, Voice } from './audioBus';
import { jitter, noise, rand, tone } from './synth';

/**
 * Sounds of the duel, at human scale: swings, shots, impacts, blows landing, bodies falling, the
 * start of the fight and the arena closing in. The screams belong to the voices (`voice.ts`).
 */

export type { ProjectileFlavour };

/** A blade or a claw cuts the air; a bigger fighter (`size` 0 … 1) swings slower and lower. */
export function meleeSwing(bus: AudioBus, at: Vec3Like | undefined, size: number): void {
  const v = bus.voice('melee-swing', { at, gain: 0.5 });
  if (!v) return;
  const seconds = jitter(0.16 + 0.1 * size, 0.15);
  v.end(
    noise(v, {
      kind: 'pink',
      seconds,
      env: { attack: seconds * 0.35, release: seconds * 0.5 },
      filter: { type: 'bandpass', freq: 2200 - 900 * size, sweep: { to: 400 }, q: 1.1 },
      gain: 0.9,
    }),
  );
}

export function projectileLaunch(
  bus: AudioBus,
  at: Vec3Like | undefined,
  flavour: ProjectileFlavour,
): void {
  const v = bus.voice(`projectile-${flavour}`, { at, gain: 0.55 });
  if (!v) return;
  v.end(LAUNCH[flavour](v));
}

const LAUNCH: Record<ProjectileFlavour, (v: Voice) => number> = {
  // The string snaps, then the arrow whistles away.
  arrow: (v) => {
    tone(v, {
      type: 'triangle',
      freq: jitter(190, 0.1),
      sweep: { to: 120, seconds: 0.08 },
      seconds: 0.02,
      env: { attack: 0.001, decay: 0.05, sustain: 0.3, release: 0.15 },
      gain: 0.5,
    });
    noise(v, {
      seconds: 0.01,
      env: { attack: 0.001, release: 0.03 },
      filter: { type: 'highpass', freq: 2500 },
      gain: 0.3,
    });
    return tone(v, {
      freq: 2600,
      sweep: { to: 1700 },
      seconds: 0.35,
      at: 0.03,
      env: { attack: 0.05, release: 0.2 },
      gain: 0.12,
      vibrato: { hz: 12, cents: 30 },
    });
  },
  // A roar of flame over a low rumble, with crackles.
  fire: (v) => {
    tone(v, {
      freq: 68,
      seconds: 0.35,
      env: { attack: 0.05, release: 0.3 },
      gain: 0.5,
      am: { hz: 24, depth: 0.5 },
    });
    for (let i = 0; i < 6; i++)
      noise(v, {
        seconds: 0.006,
        at: rand(0.02, 0.45),
        env: { attack: 0.001, release: 0.015 },
        filter: { type: 'highpass', freq: 3000 },
        gain: 0.2,
      });
    return noise(v, {
      kind: 'pink',
      seconds: 0.4,
      env: { attack: 0.06, release: 0.35 },
      filter: { type: 'lowpass', freq: 260, sweep: { to: 1100, seconds: 0.2 }, q: 1.4 },
      gain: 0.9,
    });
  },
  // A bolt of magic zaps upwards with a shimmer behind it.
  bolt: (v) => {
    const end = tone(v, {
      freq: 320,
      sweep: { to: 1900, seconds: 0.18 },
      seconds: 0.22,
      env: { attack: 0.01, release: 0.2 },
      gain: 0.35,
      fm: { ratio: 2.01, depth: 500, depthTo: 60 },
    });
    return Math.max(
      end,
      noise(v, {
        seconds: 0.2,
        env: { attack: 0.02, release: 0.25 },
        filter: { type: 'bandpass', freq: 3500, sweep: { to: 7000 }, q: 3 },
        gain: 0.15,
      }),
    );
  },
  // A boulder heaved with a grunt of effort.
  rock: (v) => {
    tone(v, {
      type: 'sawtooth',
      freq: 90,
      sweep: { to: 60 },
      seconds: 0.2,
      env: { attack: 0.03, release: 0.15 },
      gain: 0.25,
      filter: { type: 'lowpass', freq: 300 },
    });
    return noise(v, {
      kind: 'pink',
      seconds: 0.3,
      env: { attack: 0.1, release: 0.2 },
      filter: { type: 'lowpass', freq: 500, sweep: { to: 120 }, q: 1 },
      gain: 0.8,
    });
  },
  // A wet spit: a short hiss and a squelch.
  spit: (v) => {
    noise(v, {
      seconds: 0.03,
      env: { attack: 0.002, release: 0.05 },
      filter: { type: 'highpass', freq: 1800 },
      gain: 0.4,
    });
    return tone(v, {
      freq: 700,
      sweep: { to: 180, seconds: 0.09 },
      seconds: 0.04,
      at: 0.01,
      env: { attack: 0.005, release: 0.1 },
      gain: 0.4,
      fm: { ratio: 0.5, depth: 200 },
    });
  },
};

/** A projectile lands, on the world or on a body. */
export function projectileImpact(
  bus: AudioBus,
  at: Vec3Like | undefined,
  flavour: ProjectileFlavour,
): void {
  const v = bus.voice(`impact-${flavour}`, { at, gain: 0.55 });
  if (!v) return;
  let end = tone(v, {
    freq: 130,
    sweep: { to: 45, seconds: 0.1 },
    seconds: 0.05,
    env: { attack: 0.002, release: 0.18 },
    gain: 0.6,
  });
  switch (flavour) {
    case 'fire':
      end = Math.max(
        end,
        noise(v, {
          kind: 'pink',
          seconds: 0.15,
          env: { attack: 0.005, release: 0.45 },
          filter: { type: 'lowpass', freq: 2500, sweep: { to: 150, seconds: 0.5 }, q: 1 },
          gain: 0.9,
        }),
      );
      break;
    case 'bolt':
      end = Math.max(
        end,
        tone(v, {
          freq: 1400,
          sweep: { to: 300, seconds: 0.15 },
          seconds: 0.1,
          env: { attack: 0.003, release: 0.2 },
          gain: 0.3,
          fm: { ratio: 3.7, depth: 600, depthTo: 0 },
        }),
      );
      break;
    case 'spit':
      end = Math.max(
        end,
        noise(v, {
          seconds: 0.05,
          env: { attack: 0.003, release: 0.12 },
          filter: { type: 'bandpass', freq: 1200, q: 0.8 },
          gain: 0.5,
        }),
      );
      break;
    default:
      // An arrow or a rock: a crack and some debris.
      noise(v, {
        seconds: 0.012,
        env: { attack: 0.001, release: 0.03 },
        filter: { type: 'bandpass', freq: 3200, q: 1 },
        gain: 0.5,
      });
      end = Math.max(
        end,
        noise(v, {
          kind: 'pink',
          seconds: 0.06,
          env: { attack: 0.01, release: 0.15 },
          filter: { type: 'lowpass', freq: 700 },
          gain: 0.5,
        }),
      );
  }
  v.end(end);
}

/** A blow lands on a body: a thump, and a crack when it is a heavy one (`severity` 0 … 1). */
export function fighterHit(bus: AudioBus, at: Vec3Like | undefined, severity: number): void {
  const v = bus.voice('fighter-hit', { at, gain: 0.5 + 0.4 * severity, priority: 'high' });
  if (!v) return;
  let end = tone(v, {
    freq: 95,
    sweep: { to: 40, seconds: 0.09 },
    seconds: 0.05,
    env: { attack: 0.003, release: 0.2 },
    gain: 0.8,
  });
  end = Math.max(
    end,
    noise(v, {
      kind: 'pink',
      seconds: 0.04,
      env: { attack: 0.002, release: 0.09 },
      filter: { type: 'lowpass', freq: 900 + 1500 * severity },
      gain: 0.6,
    }),
  );
  if (severity > 0.4)
    end = Math.max(
      end,
      noise(v, {
        seconds: 0.01,
        env: { attack: 0.001, release: 0.04 },
        filter: { type: 'bandpass', freq: 2800, q: 1.5 },
        gain: 0.5,
      }),
    );
  v.end(end);
}

/** A body falls: two thuds, the second the heavier, and a slide of dust (`size` 0 … 1). */
export function fighterDeath(bus: AudioBus, at: Vec3Like | undefined, size: number): void {
  const v = bus.voice('fighter-death', { at, gain: 0.7, priority: 'high', reverb: 0.2 });
  if (!v) return;
  let end = tone(v, {
    freq: 110,
    sweep: { to: 45, seconds: 0.1 },
    seconds: 0.05,
    at: 0.05,
    env: { attack: 0.003, release: 0.2 },
    gain: 0.5,
  });
  end = Math.max(
    end,
    tone(v, {
      freq: 80 - 20 * size,
      sweep: { to: 30, seconds: 0.14 },
      seconds: 0.08,
      at: 0.32,
      env: { attack: 0.003, release: 0.35 + 0.2 * size },
      gain: 0.9,
    }),
  );
  end = Math.max(
    end,
    noise(v, {
      kind: 'pink',
      seconds: 0.25,
      at: 0.3,
      env: { attack: 0.01, release: 0.4 },
      filter: { type: 'lowpass', freq: 800, sweep: { to: 200 } },
      gain: 0.5,
    }),
  );
  v.end(end);
}

/** The air moves with an aura attack; the wail itself is the character's voice. */
export function auraWhoosh(bus: AudioBus, at: Vec3Like | undefined): void {
  const v = bus.voice('aura', { at, gain: 0.4, reverb: 0.4 });
  if (!v) return;
  v.end(
    noise(v, {
      kind: 'pink',
      seconds: 0.35,
      env: { attack: 0.12, release: 0.3 },
      filter: { type: 'bandpass', freq: 500, sweep: { to: 2500 }, q: 2 },
      gain: 0.7,
      am: { hz: 9, depth: 0.5 },
    }),
  );
}

/** A burst attack: an explosion with a sub thump and a long tail. */
export function burstBlast(bus: AudioBus, at: Vec3Like | undefined): void {
  const v = bus.voice('burst', { at, gain: 0.85, priority: 'high', reverb: 0.3 });
  if (!v) return;
  tone(v, {
    freq: 60,
    sweep: { to: 25, seconds: 0.4 },
    seconds: 0.15,
    env: { attack: 0.005, release: 0.4 },
    gain: 0.9,
  });
  v.end(
    noise(v, {
      kind: 'pink',
      seconds: 0.2,
      env: { attack: 0.004, release: 0.9 },
      filter: { type: 'lowpass', freq: 4000, sweep: { to: 120, seconds: 1 }, q: 0.8 },
      gain: 1,
    }),
  );
}

/**
 * A fighter in fire – burning ground, or outside the closing ring – a hiss with a few dry cracks
 * in it. The director plays one every few tenths of a second while the fire bites, not every step.
 */
export function burnCrackle(bus: AudioBus, at: Vec3Like | undefined): void {
  const v = bus.voice('burn', { at, gain: 0.4 });
  if (!v) return;
  let end = noise(v, {
    kind: 'pink',
    seconds: 0.16,
    env: { attack: 0.02, release: 0.12 },
    filter: { type: 'bandpass', freq: 900, q: 0.8 },
    gain: 0.45,
  });
  for (let i = 0; i < 3; i++)
    end = Math.max(
      end,
      noise(v, {
        seconds: 0.006,
        at: rand(0, 0.2),
        env: { attack: 0.001, release: 0.025 },
        filter: { type: 'highpass', freq: 2400 },
        gain: 0.55,
      }),
    );
  v.end(end);
}

/** The arena starts to burn: a rumble under a rising alarm. */
export function suddenDeathAlarm(bus: AudioBus): void {
  const v = bus.voice('sudden-death', { gain: 0.5, priority: 'high', reverb: 0.3 });
  if (!v) return;
  let end = noise(v, {
    kind: 'pink',
    seconds: 1.6,
    env: { attack: 0.4, release: 0.8 },
    filter: { type: 'lowpass', freq: 140 },
    gain: 0.9,
  });
  for (let i = 0; i < 3; i++)
    end = Math.max(
      end,
      tone(v, {
        type: 'sawtooth',
        freq: 220,
        sweep: { to: 440 },
        seconds: 0.35,
        at: i * 0.5,
        env: { attack: 0.02, release: 0.1 },
        gain: 0.18,
        filter: { type: 'lowpass', freq: 1500 },
      }),
    );
  v.end(end);
}

/** The count before a duel: a short bright tick, the last one a fourth higher. */
export function countTick(bus: AudioBus, n: number): void {
  const v = bus.voice('count', { gain: 0.35, priority: 'high' });
  if (!v) return;
  v.end(
    tone(v, {
      freq: n <= 1 ? 880 : 660,
      seconds: 0.05,
      env: { attack: 0.003, release: 0.12 },
      gain: 0.6,
    }),
  );
}

/** The duel is on: a drum and a horn. */
export function fightStart(bus: AudioBus): void {
  const v = bus.voice('fight-start', { gain: 0.6, priority: 'high', reverb: 0.4 });
  if (!v) return;
  tone(v, {
    freq: 150,
    sweep: { to: 40, seconds: 0.1 },
    seconds: 0.06,
    env: { attack: 0.003, release: 0.4 },
    gain: 0.8,
  });
  let end = 0;
  for (const freq of [110, 165])
    end = Math.max(
      end,
      tone(v, {
        type: 'sawtooth',
        freq,
        seconds: 0.9,
        at: 0.05,
        env: { attack: 0.08, decay: 0.3, sustain: 0.7, release: 0.5 },
        gain: 0.22,
        filter: { type: 'lowpass', freq: 400, sweep: { to: 1800, seconds: 0.5 }, q: 1.5 },
        vibrato: { hz: 5.5, cents: 8 },
      }),
    );
  v.end(end);
}

/** A footfall on grass and earth; the player's own steps are softer (`weight` 0 … 1). */
export function arenaFootfall(
  bus: AudioBus,
  at: Vec3Like | undefined,
  weight: number,
  own: boolean,
): void {
  const v = bus.voice('step', { at, priority: 'low', gain: (own ? 0.2 : 0.35) + 0.4 * weight });
  if (!v) return;
  tone(v, {
    freq: jitter(70 + 40 * (1 - weight), 0.1),
    sweep: { to: 35, seconds: 0.06 },
    seconds: 0.03,
    env: { attack: 0.002, release: 0.08 + 0.1 * weight },
    gain: 0.5,
  });
  v.end(
    noise(v, {
      kind: 'pink',
      seconds: 0.03 + 0.03 * weight,
      env: { attack: 0.003, release: 0.06 },
      filter: { type: 'lowpass', freq: 1500 - 700 * weight, q: 0.7 },
      gain: 0.6,
    }),
  );
}
